import { PERIODS_SEPARATORS } from '@/common/constants/periods';

import { parseYearMonth } from '@/modules/ved/utils/options';

type TParsedMonth = {
  year: string;
  month: number;
  labelShort: string;
};

abstract class PeriodsStrategy {
  canHandle(_sortedYearOnly: string[], _sortedMonths: TParsedMonth[]): boolean {
    throw new Error();
  }
  format(_sortedYearOnly: string[], _sortedMonths: TParsedMonth[]): string {
    throw new Error();
  }
  getPeriodString(firstPeriod: string, lastPeriod: string) {
    return `${firstPeriod} - ${lastPeriod}`;
  }
  getMonthString(month: TParsedMonth) {
    return `${month.labelShort} ${month.year}`;
  }
}

class SingleYearStrategy extends PeriodsStrategy {
  canHandle(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): boolean {
    return sortedYearOnly.length === 1 && sortedMonths.length === 0;
  }

  format(sortedYearOnly: string[]): string {
    return `${sortedYearOnly[0]}`;
  }
}

class SingleMonthStrategy extends PeriodsStrategy {
  canHandle(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): boolean {
    return sortedMonths.length === 1 && sortedYearOnly.length === 0;
  }

  format(_sortedYearOnly: string[], sortedMonths: TParsedMonth[]): string {
    const { year, labelShort } = sortedMonths[0];
    return `${labelShort} ${year}`;
  }
}

class OnlyYearsStrategy extends PeriodsStrategy {
  canHandle(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): boolean {
    return sortedYearOnly.length > 1 && sortedMonths.length === 0;
  }

  format(sortedYearOnly: string[]): string {
    return this.getPeriodString(sortedYearOnly[0], sortedYearOnly[sortedYearOnly.length - 1]);
  }
}

class YearWithAllMonthsStrategy extends PeriodsStrategy {
  canHandle(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): boolean {
    if (sortedMonths.length === 0 || sortedYearOnly.length !== 1) return false;

    const currentYear = sortedYearOnly[0];

    const yearMonths = new Map<string, Set<number>>();
    for (const { year, month } of sortedMonths) {
      if (currentYear !== year) {
        return false;
      }

      if (!yearMonths.has(year)) {
        yearMonths.set(year, new Set());
      }
      yearMonths.get(year)?.add(month);
    }

    const monthSet = yearMonths.get(currentYear);

    return !!monthSet && monthSet.size === 12;
  }

  format(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): string {
    const lastMonth = sortedMonths[11];
    const currentYear = sortedYearOnly[0];

    if (!lastMonth || !currentYear) return '';

    return this.getPeriodString(currentYear, this.getMonthString(lastMonth));
  }
}

class OnlyMonthsStrategy extends PeriodsStrategy {
  canHandle(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): boolean {
    return sortedMonths.length > 1 && sortedYearOnly.length === 0;
  }

  format(_sortedYearOnly: string[], sortedMonths: TParsedMonth[]): string {
    const firstMonth = sortedMonths[0];
    const lastMonth = sortedMonths[sortedMonths.length - 1];

    return this.getPeriodString(this.getMonthString(firstMonth), this.getMonthString(lastMonth));
  }
}

class MixedPeriodStrategy extends PeriodsStrategy {
  canHandle(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): boolean {
    return sortedMonths.length > 0 && sortedYearOnly.length > 0;
  }

  format(sortedYearOnly: string[], sortedMonths: TParsedMonth[]): string {
    const firstMonth = sortedMonths[0];
    const lastMonth = sortedMonths[sortedMonths.length - 1];
    const firstYear = sortedYearOnly[0];
    const lastYear = sortedYearOnly[sortedYearOnly.length - 1];

    const startPeriod = Number(firstMonth.year) < Number(firstYear) ? this.getMonthString(firstMonth) : firstYear;
    const lastPeriod = Number(lastMonth.year) >= Number(lastYear) ? this.getMonthString(lastMonth) : lastYear;

    return this.getPeriodString(startPeriod, lastPeriod);
  }
}

const strategies: PeriodsStrategy[] = [
  new SingleYearStrategy(),
  new SingleMonthStrategy(),
  new OnlyYearsStrategy(),
  new OnlyMonthsStrategy(),
  new YearWithAllMonthsStrategy(),
  new MixedPeriodStrategy(),
];

const sortYears = (years: Set<string>): string[] => {
  return Array.from(years).sort((a, b) => parseInt(a) - parseInt(b));
};

const sortMonths = (months: TParsedMonth[]): TParsedMonth[] => {
  return months.sort((a, b) => {
    if (a.year !== b.year) {
      return parseInt(a.year) - parseInt(b.year);
    }
    return a.month - b.month;
  });
};

const parsePeriods = (periods: string[]): { yearOnly: Set<string>; months: TParsedMonth[] } => {
  const yearOnly = new Set<string>();
  const months: TParsedMonth[] = [];

  for (const period of periods) {
    if (period.includes(PERIODS_SEPARATORS.month)) {
      const parsed = parseYearMonth(period);
      if (!isNaN(parsed.year) && !isNaN(parsed.month)) {
        months.push({
          year: parsed.year.toString(),
          month: parsed.month - 1, // M1 = индекс 0
          labelShort: parsed.labelShort,
        });
      }
    } else {
      yearOnly.add(period);
    }
  }

  return { yearOnly, months };
};

const findStrategy = (sortedYearOnly: string[], sortedMonths: TParsedMonth[]): PeriodsStrategy | null => {
  for (const strategy of strategies) {
    if (strategy.canHandle(sortedYearOnly, sortedMonths)) {
      return strategy;
    }
  }
  return null;
};

export const getBasePeriodsInfo = (periods: string[]): string => {
  if (!periods || periods.length === 0) {
    return '';
  }

  const { yearOnly, months } = parsePeriods(periods);
  const sortedYearOnly = sortYears(yearOnly);
  const sortedMonths = sortMonths(months);

  const strategy = findStrategy(sortedYearOnly, sortedMonths);
  if (strategy) {
    const label = strategy.format(sortedYearOnly, sortedMonths);
    if (!label) return '';
    const suffix = label.includes(' - ') ? ' гг.' : ' г.';
    return `${label} ${suffix}`;
  }

  return '';
};

// Период в виде текста для экспорта Excel (пробелы → _, без точек).
export const getPeriodForFilename = (periods: string[]): string => {
  const text = getBasePeriodsInfo(periods || []);
  if (!text) return '';
  const withoutSpacesAroundDash = text.replace(/\s-\s/g, '-');
  return withoutSpacesAroundDash.trim().replace(/\s+/g, '_').replace(/\./g, '');
};
